import React, { useEffect, useMemo, useState } from 'react';
import { Btn, DangerBtn, Loading, Msg, Section, Select, TextInput, panelGet, panelPost, useAdminGet } from '../kit';
import { Badge, Drawer, Labeled, inputCls } from '../email/emailKit';
import { ItemCard } from '../../whatsnew/WorldNews';
import { WhatsNewPage } from '../../whatsnew/WhatsNewPage';
import type { WnFetcher, WnItem } from '../../whatsnew/types';

/**
 * Admin → For You & What's New → "What's New · News": the curated "In the
 * world" items (Backend/whatsnew.py, panel section "whatsnew").
 *
 *   items        list (status / category / search), counts per status
 *   item_save    create / update (validated: category + type in the lists,
 *                date, http(s) source URL — required to publish)
 *   item_validate the editor's "Check" (no save)
 *   items_bulk   publish / hide / back to draft (publishing re-validates)
 *   item_delete  delete
 *   preview      the page as members see it, drafts included
 */

const S = 'whatsnew';

interface Cat { key: string; label: string; color: string; types: string[] }
interface Settings { categories: Cat[]; industries: string[] }
type Draft = Partial<WnItem> & { id?: number };

const today = () => new Date().toISOString().slice(0, 10);
const emptyDraft = (cats: Cat[]): Draft => ({
  status: 'draft', category: cats[0]?.key || '', type_label: cats[0]?.types[0] || '', item_date: today(),
  title: '', org: '', country: '', summary: '', amount: '', industry: '', source_url: '',
});
const STATUS_TONE: Record<string, 'green' | 'amber' | 'grey'> = { published: 'green', draft: 'amber', hidden: 'grey' };

const previewFetcher: WnFetcher = async (q) => {
  const p: Record<string, string> = {};
  if (q.category && q.category !== 'all') p.category = q.category;
  if (q.industry) p.industry = q.industry;
  if (q.from) p.from = q.from;
  if (q.to) p.to = q.to;
  const { data, error } = await panelGet(S, 'preview', p);
  return { data, error };
};

const Editor: React.FC<{ draft: Draft; settings: Settings; onClose: () => void; onSaved: () => void }> =
  ({ draft: initial, settings, onClose, onSaved }) => {
  const [d, setD] = useState<Draft>(initial);
  const [note, setNote] = useState<{ kind: 'error' | 'ok'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const cat = settings.categories.find(c => c.key === d.category);
  const set = (k: keyof Draft, v: any) => setD(x => ({ ...x, [k]: v }));
  const setCategory = (key: string) => {
    const c = settings.categories.find(x => x.key === key);
    setD(x => ({ ...x, category: key,
      type_label: c && c.types.includes(x.type_label || '') ? x.type_label : (c?.types[0] || '') }));
  };
  const save = async (status?: string) => {
    setBusy(true); setNote(null);
    const { data, error } = await panelPost(S, 'item_save', { ...d, ...(status ? { status } : {}) });
    setBusy(false);
    if (error) { setNote({ kind: 'error', text: error }); return; }
    setD({ ...d, ...(data?.item || {}) });
    setNote({ kind: 'ok', text: status === 'published' || data?.item?.status === 'published'
      ? 'Saved and published — members see it within five minutes.' : 'Saved.' });
    onSaved();
  };
  const check = async () => {
    setNote(null);
    const { error } = await panelPost(S, 'item_validate', { ...d, status: 'published' });
    setNote(error ? { kind: 'error', text: error } : { kind: 'ok', text: 'Ready to publish.' });
  };
  const sumLen = (d.summary || '').length;
  return (
    <Drawer title={d.id ? `Edit item #${d.id}` : 'New item'}
      subtitle={d.id ? `Status: ${d.status}` : 'Saved as a draft until you publish'} onClose={onClose}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-white rounded-xl border border-grayscale-300 p-4">
        <Labeled label="Category">
          <select className={inputCls} value={d.category} onChange={e => setCategory(e.target.value)}>
            {settings.categories.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
            {!cat && d.category && <option value={d.category}>{d.category} (not listed)</option>}
          </select>
        </Labeled>
        <Labeled label="Type label">
          <select className={inputCls} value={d.type_label} onChange={e => set('type_label', e.target.value)}>
            {(cat?.types || []).map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </Labeled>
        <Labeled label="Title" hint="up to 200 characters" span="sm:col-span-2">
          <input className={inputCls} value={d.title || ''} maxLength={200} onChange={e => set('title', e.target.value)} />
        </Labeled>
        <Labeled label="Organisation">
          <input className={inputCls} value={d.org || ''} maxLength={160} onChange={e => set('org', e.target.value)}
            placeholder="Massachusetts Institute of Technology" />
        </Labeled>
        <Labeled label="Country / region">
          <input className={inputCls} value={d.country || ''} maxLength={80} onChange={e => set('country', e.target.value)}
            placeholder="United States" />
        </Labeled>
        <Labeled label="Summary" hint={`one or two lines · ${sumLen}/400`} span="sm:col-span-2">
          <textarea className={`${inputCls} h-20 py-2`} value={d.summary || ''} maxLength={400}
            onChange={e => set('summary', e.target.value)} />
        </Labeled>
        <Labeled label="Date">
          <input type="date" className={inputCls} value={d.item_date || ''} max={today()}
            onChange={e => set('item_date', e.target.value)} />
        </Labeled>
        <Labeled label="Amount" hint="optional, e.g. $24M deal">
          <input className={inputCls} value={d.amount || ''} maxLength={40} onChange={e => set('amount', e.target.value)} />
        </Labeled>
        <Labeled label="Industry tag">
          <select className={inputCls} value={d.industry || ''} onChange={e => set('industry', e.target.value)}>
            <option value="">— none —</option>
            {settings.industries.map(i => <option key={i} value={i}>{i}</option>)}
            {d.industry && !settings.industries.includes(d.industry) && (
              <option value={d.industry}>{d.industry} (not listed)</option>)}
          </select>
        </Labeled>
        <Labeled label="Source URL" hint="required to publish">
          <input className={inputCls} value={d.source_url || ''} onChange={e => set('source_url', e.target.value)}
            placeholder="https://…" />
        </Labeled>
      </div>
      <Section title="Preview">
        <div className="max-w-xl">
          <ItemCard item={{ ...(d as WnItem), id: d.id || 0 }} color={cat?.color || '#80653F'} onSource={() => {}} />
        </div>
      </Section>
      {note && <Msg kind={note.kind}>{note.text}</Msg>}
      <div className="flex gap-2 flex-wrap">
        <Btn onClick={check} disabled={busy}>Check</Btn>
        <Btn onClick={() => save()} disabled={busy}>{d.id ? 'Save' : 'Save draft'}</Btn>
        {d.status !== 'published' && <Btn kind="primary" onClick={() => save('published')} disabled={busy}>Publish</Btn>}
        {d.status === 'published' && <Btn onClick={() => save('hidden')} disabled={busy}>Hide</Btn>}
      </div>
    </Drawer>
  );
};

const News: React.FC = () => {
  const st = useAdminGet<any>(S, 'settings');
  const [status, setStatus] = useState('all');
  const [category, setCategory] = useState('all');
  const [q, setQ] = useState('');
  const [qApplied, setQApplied] = useState('');
  useEffect(() => { const t = setTimeout(() => setQApplied(q), 300); return () => clearTimeout(t); }, [q]);
  const list = useAdminGet<any>(S, 'items', { status, category, q: qApplied }, [status, category, qApplied]);
  const [sel, setSel] = useState<Set<number>>(new Set());
  const [editing, setEditing] = useState<Draft | null>(null);
  const [preview, setPreview] = useState(false);
  const [note, setNote] = useState<{ kind: 'error' | 'ok'; text: string } | null>(null);

  const settings: Settings | null = st.data ? { categories: st.data.categories, industries: st.data.industries } : null;
  const items: (WnItem & { category_label: string; updated_by?: string })[] = list.data?.items || [];
  const counts = list.data?.counts || {};
  useEffect(() => { setSel(new Set()); }, [status, category, qApplied]);
  const allOn = items.length > 0 && items.every(i => sel.has(i.id));
  const toggle = (id: number) => setSel(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const bulk = async (newStatus: string) => {
    setNote(null);
    const { data, error } = await panelPost(S, 'items_bulk', { ids: Array.from(sel), status: newStatus });
    if (error) { setNote({ kind: 'error', text: error }); return; }
    const sk = (data?.skipped || []) as { title: string; reason: string }[];
    setNote({ kind: sk.length ? 'error' : 'ok',
      text: `${data?.updated || 0} item${data?.updated === 1 ? '' : 's'} set to ${newStatus}.`
        + (sk.length ? ` Not published: ${sk.map(x => `"${x.title}" (${x.reason})`).join('; ')}` : '') });
    setSel(new Set()); list.reload();
  };
  const del = async (ids: number[]) => {
    const { data, error } = await panelPost(S, 'item_delete', { ids });
    setNote(error ? { kind: 'error', text: error } : { kind: 'ok', text: `Deleted ${data?.deleted || 0}.` });
    setSel(new Set()); list.reload();
  };
  const quick = async (it: WnItem, s2: string) => {
    const { data, error } = await panelPost(S, 'items_bulk', { ids: [it.id], status: s2 });
    const sk = data?.skipped?.[0];
    setNote(error ? { kind: 'error', text: error } : sk ? { kind: 'error', text: `Not published: ${sk.reason}` } : null);
    list.reload();
  };

  const catOptions = useMemo(() => ['all', ...(settings?.categories.map(c => c.key) || [])], [settings]);
  const catLabels = useMemo(() => Object.fromEntries([['all', 'All categories'],
    ...(settings?.categories.map(c => [c.key, c.label]) || [])]), [settings]);

  if (st.loading && !st.data) return <Loading />;
  if (st.error) return <Msg kind="error">{st.error}</Msg>;

  return (
    <div className="flex flex-col gap-4">
      <Section title="In the world · curated news"
        right={<div className="flex gap-2">
          <Btn onClick={() => setPreview(true)}>Preview page</Btn>
          <Btn kind="primary" onClick={() => settings && setEditing(emptyDraft(settings.categories))}>New item</Btn>
        </div>}>
        <div className="flex gap-2 flex-wrap items-center">
          <Select value={status} onChange={setStatus} options={['all', 'published', 'draft', 'hidden']}
            labels={{ all: `All (${(counts.published || 0) + (counts.draft || 0) + (counts.hidden || 0)})`,
              published: `Published (${counts.published || 0})`, draft: `Drafts (${counts.draft || 0})`,
              hidden: `Hidden (${counts.hidden || 0})` }} />
          <Select value={category} onChange={setCategory} options={catOptions} labels={catLabels} />
          <TextInput value={q} onChange={setQ} placeholder="Search title, organisation, summary…" wide />
        </div>
        {sel.size > 0 && (
          <div className="flex items-center gap-2 flex-wrap rounded-lg bg-primary-100 px-3 py-2 text-sm">
            <span className="font-medium">{sel.size} selected</span>
            <Btn small kind="primary" onClick={() => bulk('published')}>Publish</Btn>
            <Btn small onClick={() => bulk('hidden')}>Hide</Btn>
            <Btn small onClick={() => bulk('draft')}>Back to draft</Btn>
            <DangerBtn small onConfirm={() => del(Array.from(sel))}>Delete</DangerBtn>
            <button type="button" className="text-xs underline" onClick={() => setSel(new Set())}>clear</button>
          </div>
        )}
        {note && <Msg kind={note.kind}>{note.text}</Msg>}
        {list.error && <Msg kind="error">{list.error}</Msg>}
        {list.loading && !list.data ? <Loading /> : items.length === 0 ? (
          <div className="text-sm text-grayscale-1100 py-6 text-center">
            No items{status !== 'all' || category !== 'all' || qApplied ? ' match these filters' : ' yet — add the first one with New item'}.
          </div>
        ) : (
          <div className="overflow-auto rounded-lg border border-grayscale-300 max-h-[36rem]">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-grayscale-200 text-xs font-grotesk text-grayscale-1200">
                <tr>
                  <th className="px-3 py-2 w-8">
                    <input type="checkbox" checked={allOn} aria-label="Select all"
                      onChange={e => setSel(e.target.checked ? new Set(items.map(i => i.id)) : new Set())} />
                  </th>
                  {['Date', 'Status', 'Category', 'Type', 'Title', 'Organisation', 'Industry', ''].map(h => (
                    <th key={h} className="text-left px-3 py-2 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map(it => (
                  <tr key={it.id} className="border-t border-grayscale-200 hover:bg-grayscale-100">
                    <td className="px-3 py-1.5"><input type="checkbox" checked={sel.has(it.id)} onChange={() => toggle(it.id)}
                      aria-label={`Select ${it.title}`} /></td>
                    <td className="px-3 py-1.5 whitespace-nowrap">{it.item_date}</td>
                    <td className="px-3 py-1.5"><Badge tone={STATUS_TONE[it.status] || 'grey'}>{it.status}</Badge></td>
                    <td className="px-3 py-1.5 whitespace-nowrap">{it.category_label}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap">{it.type_label}</td>
                    <td className="px-3 py-1.5 max-w-[24rem] truncate" title={it.title}>
                      <button type="button" className="text-left hover:underline" onClick={() => setEditing({ ...it })}>{it.title}</button>
                    </td>
                    <td className="px-3 py-1.5 max-w-[12rem] truncate" title={it.org}>{it.org || '—'}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap">{it.industry || '—'}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap flex gap-1.5">
                      <Btn small onClick={() => setEditing({ ...it })}>Edit</Btn>
                      {it.status !== 'published'
                        ? <Btn small kind="primary" onClick={() => quick(it, 'published')}>Publish</Btn>
                        : <Btn small onClick={() => quick(it, 'hidden')}>Hide</Btn>}
                      {it.source_url && <a href={it.source_url} target="_blank" rel="noopener noreferrer"
                        className="text-xs underline self-center text-grayscale-1100">source</a>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
      {editing && settings && (
        <Editor draft={editing} settings={settings} onClose={() => setEditing(null)} onSaved={() => list.reload()} />
      )}
      {preview && (
        <Drawer title="What's New — preview" subtitle="As members see it, with drafts included (marked)." onClose={() => setPreview(false)}>
          <div className="flex flex-col min-h-[40rem]">
            <WhatsNewPage preview fetcher={previewFetcher} />
          </div>
        </Drawer>
      )}
    </div>
  );
};

export default News;
